import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { IconArrowUp, IconMinus } from '@tabler/icons-react';
import { PlusIcon } from '../icons';
import type { FormField, FormFieldOption } from '../../types';
import { generateFieldId } from '../../lib/form-templates';

/** One inline option editor shared by the canvas and standalone typed settings. */
interface ChoiceOptionsEditorProps {
  field: Extract<FormField, { type: 'choice_single' | 'choice_multi' }>;
  onChange: (patch: Partial<FormField>) => void;
}

const PAGE_SIZE = 20;
const textActionClass = 'inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt disabled:cursor-default disabled:opacity-30';

const actionClass = 'flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt disabled:cursor-default disabled:opacity-30';

/** Edits labels in place while keeping each stored option identity and value intact. */
export function ChoiceOptionsEditor({ field, onChange }: ChoiceOptionsEditorProps): React.ReactElement {
  const root = useRef<HTMLFieldSetElement>(null);
  const pasteErrorId = useId();
  const [pasteError, setPasteError] = useState<{ fieldId: string; optionId: string; message: string } | null>(null);
  const focusOptionId = useRef<string | null>(null);
  const [query, setQuery] = useState('');
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const bounded = field.options.length > PAGE_SIZE;
  // Keep the active row mounted while its new wording diverges from the search.
  const matches = field.options.map((option, index) => ({ option, index })).filter(({ option }) => !bounded || option.id === editingOptionId || option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const currentPage = Math.min(page, Math.max(0, Math.ceil(matches.length / PAGE_SIZE) - 1));
  const rows = bounded ? matches.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE) : matches;
  const multiple = field.type === 'choice_multi';
  const metric = field.type === 'choice_single' && field.rating;

  /** Detach the source only when the authored option content or ordering actually changes. */
  const updateOptions = (options: FormFieldOption[]) => {
    setPasteError(null);
    onChange({ options, choiceLibrary: undefined, otherOptionId: options.some(option => option.id === field.otherOptionId) ? field.otherOptionId : undefined });
  };

  /** Split a plain-text list at the cursor while retaining existing option identities and wording. */
  const pasteOptions = (event: React.ClipboardEvent<HTMLTextAreaElement>, index: number) => {
    if (metric) return;
    const lines = event.clipboardData.getData('text/plain').split(/\r\n|\r|\n/).map(line => line.trim()).filter(Boolean);
    if (lines.length < 2) return;
    event.preventDefault();
    const input = event.currentTarget;
    const target = field.options[index];
    lines[0] = target.label.slice(0, input.selectionStart) + lines[0];
    lines[lines.length - 1] += target.label.slice(input.selectionEnd);
    if (field.options.length + lines.length - 1 > 1000) {
      setPasteError({ fieldId: field.id, optionId: target.id, message: 'Use up to 1,000 options.' });
      return;
    }
    if (lines.some(label => label.length > 1000)) {
      setPasteError({ fieldId: field.id, optionId: target.id, message: 'Keep each option to 1,000 characters or fewer.' });
      return;
    }
    const inserted = lines.slice(1).map(label => {
      const id = generateFieldId();
      return { id, label, value: `option_${id}` };
    });
    setQuery('');
    setEditingOptionId(null);
    setPage(Math.floor((index + 1) / PAGE_SIZE));
    focusOptionId.current = inserted[0].id;
    updateOptions([
      ...field.options.slice(0, index),
      { ...target, label: lines[0] },
      ...inserted,
      ...field.options.slice(index + 1),
    ]);
  };

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return undefined;
    /** Refit wrapped labels without changing their content or the current editing focus. */
    const resize = () => {
      element.querySelectorAll('textarea').forEach(input => {
        input.style.height = 'auto';
        input.style.height = `${input.scrollHeight}px`;
        if (focusOptionId.current === input.dataset.optionId) {
          input.focus();
          focusOptionId.current = null;
        }
      });
    };
    let width = element.clientWidth;
    let frame: number | null = null;
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = null; resize(); });
    });
    observer?.observe(element);
    return () => {
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [field.options, query, currentPage]);

  return <fieldset ref={root} className="min-w-0 space-y-2"
    aria-label={metric ? 'Rating options' : multiple ? 'Multiple choice options' : 'Single choice options'}>
    <legend className="mb-2 text-sm text-muted-foreground">{metric ? 'Rating' : multiple ? 'Select multiple' : 'Select one'}</legend>
    {pasteError?.fieldId === field.id && <p id={pasteErrorId} role="alert" className="text-sm text-danger">{pasteError.message}</p>}
    {bounded && <input type="search" aria-label="Search options" placeholder="Search options" value={query} onKeyDown={event => { if (event.key === 'Enter') event.preventDefault(); }} onChange={event => { setQuery(event.target.value); setEditingOptionId(null); setPage(0); }}
      className="min-h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-volt" />}
    <div className={bounded ? 'max-h-80 space-y-2 overflow-y-auto overscroll-contain' : 'space-y-2'}>
    {rows.map(({ option, index }) => <div key={option.id} className="flex items-start gap-1">
      <div className="flex min-w-0 flex-1 items-start gap-3 rounded-lg border border-border bg-background px-3 py-3 focus-within:bg-surface-hover focus-within:ring-2 focus-within:ring-volt">
        <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 shrink-0 text-muted-foreground">
          {multiple ? <rect x="2" y="2" width="16" height="16" rx="2" /> : <circle cx="10" cy="10" r="8" />}
        </svg>
        <textarea rows={1} aria-label={`Option ${index + 1}`} data-option-id={option.id} placeholder="Option"
          aria-invalid={pasteError?.fieldId === field.id && pasteError.optionId === option.id || undefined}
          aria-describedby={pasteError?.fieldId === field.id && pasteError.optionId === option.id ? pasteErrorId : undefined}
          onPaste={event => pasteOptions(event, index)}
          className="min-h-5 min-w-0 w-full resize-none overflow-hidden bg-transparent text-sm leading-5 text-foreground placeholder:text-muted-foreground focus:outline-none"
          value={option.label} onFocus={() => setEditingOptionId(option.id)} onChange={(event) => updateOptions(field.options.map(current => current.id === option.id ? { ...current, label: event.target.value } : current))} />
      </div>
      <div className="flex shrink-0 items-center pt-1">
        <button type="button" aria-label={`Move option ${index + 1} up`} disabled={index === 0 || option.id === field.otherOptionId} className={actionClass}
          onClick={() => { const options = [...field.options]; [options[index - 1], options[index]] = [options[index], options[index - 1]]; updateOptions(options); }}>
          <IconArrowUp aria-hidden="true" className="h-4 w-4" />
        </button>
        <button type="button" aria-label={`Remove option ${index + 1}`} className={actionClass}
          onClick={() => updateOptions(field.options.filter(current => current.id !== option.id))}>
          <IconMinus aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </div>)}
    </div>
    {bounded && <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
      <span role="status">{matches.length ? `${currentPage * PAGE_SIZE + 1} to ${Math.min((currentPage + 1) * PAGE_SIZE, matches.length)} of ${matches.length}` : 'No matches'}</span>
      {matches.length > PAGE_SIZE && <div className="flex gap-1">
        <button type="button" className={textActionClass} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button>
        <button type="button" className={textActionClass} disabled={(currentPage + 1) * PAGE_SIZE >= matches.length} onClick={() => setPage(currentPage + 1)}>Next</button>
      </div>}
    </div>}
    <button type="button" className={textActionClass}
      onClick={() => {
        const id = generateFieldId();
        focusOptionId.current = id;
        setQuery('');
        setEditingOptionId(null);
        setPage(Math.floor(field.options.length / PAGE_SIZE));
        const added = { id, label: '', value: `option_${id}` };
        const other = field.options.find(option => option.id === field.otherOptionId);
        updateOptions([...field.options.filter(option => option.id !== field.otherOptionId), added, ...(other ? [other] : [])]);
      }}>
      <PlusIcon aria-hidden="true" className="h-4 w-4" />Add option
    </button>
    {!metric && !field.otherOptionId && field.options.length < 1000 && <button type="button" className={textActionClass} onClick={() => {
      const id = generateFieldId();
      onChange({ options: [...field.options, { id, label: 'Other', value: `option_${id}` }], otherOptionId: id, choiceLibrary: undefined });
    }}><PlusIcon aria-hidden="true" className="h-4 w-4" />Add Other</button>}
  </fieldset>;
}
